import { BottomSheetModal } from '@gorhom/bottom-sheet/src';
import {
  AdvanceQualificationMethod,
  Agreement,
  BackupPaymentMethod,
  InlineAlertType,
  LendingPaymentSource,
} from '@varo/graphql-schema-types';
import BackUpPaymentsBottomSheet from 'features/advance/components/BackUpPaymentOptionsBottomSheet/BackUpPaymentOptionsBottomSheet';
import { mapLendingPaymentSourceToString } from 'features/advance/helpers/Advance.helpers';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import { InlineAlert } from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItemGroup, {
  ListItemGroupItem,
} from 'shared/components/vxl/ListItemGroup/ListItemGroup';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import ExpressiveLgText from 'shared/components/vxl/typography/expressive/ExpressiveLgText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';

interface AdvanceReviewProps {
  reviewListItems: ListItemGroupItem[];
  formattedAdvanceAmount: string;
  submitApplication: () => void;
  dynamicAgreements?: Agreement[];
  qualificationMethod: AdvanceQualificationMethod;
  dynamicAgreementsForDisbursements?: Agreement[];
  paymentSource: LendingPaymentSource;
  backupPaymentMethods: BackupPaymentMethod[];
}

const ReviewContent = ({
  reviewListItems,
  formattedAdvanceAmount,
  submitApplication,
  qualificationMethod,
  dynamicAgreementsForDisbursements,
  paymentSource,
  backupPaymentMethods,
}: AdvanceReviewProps) => {
  const [isDebitAgreementChecked, setIsDebitAgreementChecked] = useState(false);
  const [isAchChecked, setAchChecked] = useState(false);

  const backUpPaymentBottomSheetRef = useRef<BottomSheetModal>(null);
  const dismissToTab = useDismissToRootTab();

  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'review',
  });

  const subcategory =
    qualificationMethod === AdvanceQualificationMethod.ExternalAccount
      ? ''
      : 'va_disbursement';
  const achAuthorization =
    qualificationMethod === AdvanceQualificationMethod.ExternalAccount
      ? 'true'
      : 'false';

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-advance/va-review',
    customAttributes: {
      subcategory: subcategory,
      ach_authorization: achAuthorization,
      debit_card_authorization: isDebitAgreementChecked ? 'true' : 'false',
    },
  });

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <View style={styles.contentSubContainer}>
          <View style={[styles.paddingTop, styles.horizontalPadding]}>
            <TitleLgText
              numberOfLines={0}
              style={{ marginBottom: theme.spacing.s }}
            >
              {translatedText('title')}
            </TitleLgText>
            <BodyBaseText
              numberOfLines={0}
              style={{ marginBottom: theme.spacing.m }}
            >
              {translatedText('body')}
            </BodyBaseText>
            <BodySmText
              numberOfLines={0}
              style={{
                color: theme.color.text.secondary,
                marginBottom: theme.spacing.s,
              }}
            >
              {translatedText('amountTitle')}
            </BodySmText>

            <ExpressiveLgText
              numberOfLines={0}
              style={{
                marginBottom: theme.spacing.m,
              }}
            >
              {formattedAdvanceAmount}
            </ExpressiveLgText>
            <HairLineDivider />
          </View>
          <ListItemGroup items={reviewListItems} />

          {qualificationMethod ===
          AdvanceQualificationMethod.ExternalAccount ? (
            <>
              <View style={[styles.paddingTop, styles.horizontalPadding]}>
                <TitleMdText
                  numberOfLines={0}
                  style={{ marginBottom: theme.spacing.s }}
                >
                  {translatedText('backupPaymentTermsTitle')}
                </TitleMdText>
              </View>

              <View style={styles.inLineAlertContainer}>
                <InlineAlert
                  alertType={InlineAlertType.Info}
                  titleText={translatedText('backupMethodsTitle')}
                  bodyText={translatedText('backupMethodsNote')}
                  onCardPress={() => {
                    backUpPaymentBottomSheetRef.current?.present();
                  }}
                  isImageVisible={false}
                  accessibilityLabel="Alert to view backup payment methods"
                />
              </View>

              <View>
                {dynamicAgreementsForDisbursements?.map((agreement) => {
                  return (
                    <View style={styles.agreementContainer} key={agreement.id}>
                      <TitleSmText numberOfLines={0}>
                        {agreement.title}
                      </TitleSmText>

                      <BodyBaseText numberOfLines={0}>
                        <HTMLRenderer
                          htmlAsString={
                            agreement.resource?.textDocumentHtml ?? ''
                          }
                          tagsStyles={agreementContent}
                        />
                      </BodyBaseText>
                    </View>
                  );
                })}
              </View>
              <Pressable
                style={[styles.horizontalPadding, styles.tAndC]}
                onPress={() => {
                  setIsDebitAgreementChecked(!isDebitAgreementChecked);
                }}
              >
                <Checkbox
                  analyticsId="debitAgreementCheckboxClicked"
                  customAttributes={{
                    subcategory: subcategory,
                  }}
                  checked={isDebitAgreementChecked}
                  onSelectedStateChange={() => {
                    setIsDebitAgreementChecked(!isDebitAgreementChecked);
                  }}
                  ariaLabel="debit agreement checkbox"
                />

                <View style={styles.termsAndCondition}>
                  <BodyBaseText numberOfLines={0}>
                    {translatedText('lightningDebitTermsAndConditions')}
                  </BodyBaseText>
                </View>
              </Pressable>

              <Pressable
                onPress={() => {
                  setAchChecked(!isAchChecked);
                }}
                style={[styles.horizontalPadding, styles.tAndC]}
              >
                <Checkbox
                  analyticsId="achAgreementCheckboxClicked"
                  customAttributes={{ subcategory: subcategory }}
                  checked={isAchChecked}
                  onSelectedStateChange={() => {
                    setAchChecked(!isAchChecked);
                  }}
                  ariaLabel="ach agreement checkbox"
                />

                <View style={styles.termsAndCondition}>
                  <BodyBaseText numberOfLines={0}>
                    {translatedText('lightningTermsAndConditions')}
                  </BodyBaseText>
                </View>
              </Pressable>
            </>
          ) : null}

          <View style={styles.inLineAlertContainer}>
            <InlineAlert
              alertType={InlineAlertType.Info}
              bodyText={translatedText('noteV2')}
              isImageVisible={false}
            />
          </View>
        </View>
      </ScrollView>

      <View style={styles.footerContainer}>
        <Button
          isDisabled={
            qualificationMethod === AdvanceQualificationMethod.DirectDeposit
              ? false
              : !isDebitAgreementChecked || !isAchChecked
          }
          label={translatedText('buttons.getAdvance')}
          onPress={submitApplication}
          analyticsId="getAdvanceClicked"
          customAttributes={{
            subcategory: subcategory,
            ach_authorization: achAuthorization,
            debit_card_authorization: isDebitAgreementChecked
              ? 'true'
              : 'false',
            amount: formattedAdvanceAmount
              ? formattedAdvanceAmount.replace('$', '')
              : '',
            autopay_method: paymentSource
              ? mapLendingPaymentSourceToString(paymentSource)
              : 'undefined',
          }}
          size="LARGE"
          type="PRIMARY"
          width="FULL"
        />
        <Button
          label={translatedText('buttons.cancel')}
          onPress={() => {
            dismissToTab(Tabs.HOME);
          }}
          analyticsId="getAdvanceFlowExited"
          customAttributes={{
            subcategory: subcategory,
            ach_authorization: achAuthorization,
            debit_card_authorization: isDebitAgreementChecked.toString(),
          }}
          size="LARGE"
          type="SECONDARY"
          width="FULL"
        />
      </View>

      {backupPaymentMethods ? (
        <BackUpPaymentsBottomSheet
          testID="backup-payment-options-bottom-sheet"
          paymentOptions={backupPaymentMethods}
          ref={backUpPaymentBottomSheetRef}
          analyticsObj={{
            id: 'backup-payment-options',
            customAttributes: {
              source: 'va_review',
            },
          }}
        />
      ) : null}
    </View>
  );
};

const agreementContent = StyleSheet.create({
  // Disabled ESLint for unused styles in this file, as they're passed to the HTMLRenderer component.
  // eslint-disable-next-line react-native/no-unused-styles
  p: {
    color: theme.color.text.primary,
    fontFamily: VaroFontNames.NeueHaasGroteskDisplayProRomanNormal,
    fontSize: theme.spacing.m,
    letterSpacing: 0.32,
    lineHeight: theme.spacing.l,
    margin: 0,
    paddingBottom: theme.spacing.m, // Add padding to ensure text isn't cut off
  },
});

const styles = StyleSheet.create({
  agreementContainer: {
    flexGrow: 1,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },

  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
  },
  contentContainer: { flexGrow: 1 },
  contentSubContainer: {
    flexBasis: '0%',
    flexGrow: 1,
    flexShrink: 1,
    overflowX: 'hidden',
  },
  footerContainer: {
    backgroundColor: 'white',
    borderTopColor: theme.color.border.primarySolid,
    borderTopWidth: 1,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  horizontalPadding: {
    paddingHorizontal: theme.spacing.m,
  },
  inLineAlertContainer: {
    padding: theme.spacing.m,
  },
  paddingTop: {
    paddingTop: theme.spacing.m,
  },
  tAndC: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  termsAndCondition: {
    flexShrink: 1,
  },
});

export default ReviewContent;
